import jQuery from 'jquery';
import { throttle } from 'lodash';

const DEFAULT_RENEWAL_RETRY_DELAY = 5000;

export interface TokenPayload {
  token: string;
  allow_annotate: boolean;
  allow_update: boolean;
}
/**
 *  Handles sending / receiving / requesting JWT tokens for the
 *  collaboration server.
 */
export default class TokenManager {
  collaborationServer: any;
  url: string;
  collection: string;
  documentId: string;
  tokenLifetime: number;
  onTokenUpdated?: (token: TokenPayload | null) => void;
  getAuthorization?: () => string;
  token: string | null;
  timer: ReturnType<typeof setTimeout> | null;
  throttledRenewToken: ReturnType<typeof throttle>;
  renewalRequest: JQuery.jqXHR | null;
  renewalRetryDelayExponentialBackoff: number;
  onFailure?: () => void;

  constructor(
    collaborationServer: any,
    baseURL: string,
    collection: string,
    documentId: string,
    tokenLifetime: number,
    onTokenUpdated?: (token: TokenPayload | null) => void,
    getAuthorization?: () => string,
    onFailure?: () => void
  ) {
    this.collaborationServer = collaborationServer;
    this.url = this.tokenURL(baseURL);
    this.collection = collection;
    this.documentId = documentId;
    this.tokenLifetime = tokenLifetime;
    this.onTokenUpdated = onTokenUpdated;
    this.getAuthorization = getAuthorization;
    this.throttledRenewToken = throttle(this.renewToken.bind(this), 4000);
    this.renewalRequest = null;
    this.onFailure = onFailure;
  }

  tokenURL = (baseURL: string) => {
    const splitURL = baseURL.split('?');
    return `${splitURL[0]}.token${splitURL[1] ? `?${splitURL[1]}` : ''}`;
  };

  connect(callback?: () => void) {
    if (this.tokenLifetime) {
      this.timer = setTimeout(() => this.renewToken(), this.tokenLifetime);
    }

    this.collaborationServer.addEventListener(
      'connectionstatechange',
      this.sendCurrentToken
    );
    this.renewToken(callback);

    return this;
  }

  sendCurrentToken = (event: { isConnected: boolean }) => {
    if (!event.isConnected) return;

    if (this.token) {
      this.sendToken(this.token);
    }
  };

  sendToken(token: string) {
    this.token = token;

    if (this.collaborationServer.isConnected) {
      this.collaborationServer.socket.send(
        JSON.stringify({
          type: 'token',
          collection: this.collection,
          documentId: this.documentId,
          token: token,
        })
      );
    }
  }

  headersWithAuth() {
    const headers: JQuery.AjaxSettings['headers'] = {};
    const authorization = this.getAuthorization && this.getAuthorization();

    if (authorization) {
      headers.authorization = `Bearer ${authorization}`;
    }

    return headers;
  }

  renewToken(callback?: () => void) {
    this.renewalRequest = jQuery
      .get({ url: this.url, headers: this.headersWithAuth() })
      .done(data => {
        this.renewalRetryDelayExponentialBackoff = DEFAULT_RENEWAL_RETRY_DELAY;
        this.renewalRequest = null;
        this.sendToken(data.token);
        this._clearTimer();

        if (this.tokenLifetime) {
          this.timer = setTimeout(() => this.renewToken(), this.tokenLifetime);
        }
        callback && callback();
        this.onTokenUpdated && this.onTokenUpdated(data);
      })
      .fail((response, message) => {
        this.renewalRequest = null;
        this._clearTimer();
        this.token = null;
        this.onTokenUpdated && this.onTokenUpdated(null);

        // If response status is 401, 403, or 404 (the user was
        // removed), stop trying -- we're no longer
        // authorized. Otherwise, wait a bit and try again.
        if ([401, 403, 404].includes(response.status)) {
          typeof this.onFailure === 'function' && this.onFailure();
        } else {
          console.log(
            `Token renewal failed with status ${
              response.status
            }. Retrying after ${DEFAULT_RENEWAL_RETRY_DELAY / 1000} seconds.`
          );
          this.timer = setTimeout(
            () => this.renewToken(callback),
            DEFAULT_RENEWAL_RETRY_DELAY
          );
        }
      });
    return true;
  }

  disconnect() {
    if (this.renewalRequest) {
      this.renewalRequest.abort();
    }
    this._clearTimer();
    this.throttledRenewToken.cancel();
    this.collaborationServer.removeEventListener(
      'connectionstatechange',
      this.sendCurrentToken
    );
  }

  _clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
